/**
 * BinRangeAgGridHook
 *
 * AG Grid for the BIN / IIN Ranges screen (PlatformWeb.SchemeLive.BinRanges).
 * Replaces a plain unpaginated <table> that rendered every matching row as
 * its own DOM node — real BIN tables run into the thousands of rows (the
 * JCB IIN File load alone seeds 2,616), which made the page slow to render
 * and impossible to scan. AG Grid virtualizes rows and paginates client-side.
 *
 * LiveView events received:
 *  "update_bin_range_data" – { rows: [...] } – pushed after filter changes.
 *
 * LiveView events pushed:
 *  "edit" – { id: <id> } – opens the existing edit form for that row
 *           (same event name/shape the old table's "Edit" link already used).
 */
const BinRangeAgGridHook = {
  mounted() {
    const container = this.el;
    const rows = JSON.parse(container.dataset.rows || "[]");
    const self = this;

    const statusBadge = (value) => {
      const colours = {
        active: "#16A34A",
        draft: "#2563EB",
        suspended: "#D97706",
        inactive: "#6B7280",
      };
      const bg = colours[value] || "#9CA3AF";
      return `<span style="background:${bg};color:#fff;padding:2px 8px;border-radius:9999px;
        font-size:11px;font-weight:600;white-space:nowrap;text-transform:capitalize;">${value || ""}</span>`;
    };

    const actionsRenderer = (params) => {
      if (!params.data) return "";
      const { id } = params.data;
      const btn = document.createElement("button");
      btn.textContent = "Edit";
      btn.style.cssText = "color:#4f46e5;font-size:11px;cursor:pointer;background:none;border:none;padding:0;";
      btn.addEventListener("click", (e) => {
        e.stopPropagation();
        self.pushEvent("edit", { id: String(id) });
      });
      return btn;
    };

    const columnDefs = [
      { headerName: "Start BIN", field: "start_bin", width: 130, cellClass: "font-mono" },
      { headerName: "End BIN", field: "end_bin", width: 130, cellClass: "font-mono" },
      { headerName: "Scheme", field: "scheme_name", width: 140 },
      { headerName: "Issuer", field: "issuer_name", flex: 1, minWidth: 160 },
      { headerName: "Country", field: "country_code", width: 100 },
      {
        headerName: "Status",
        field: "status",
        width: 110,
        cellRenderer: (p) => statusBadge(p.value),
      },
      {
        headerName: "Actions",
        colId: "actions",
        pinned: "right",
        width: 90,
        sortable: false,
        filter: false,
        floatingFilter: false,
        resizable: false,
        cellRenderer: actionsRenderer,
      },
    ];

    const gridOptions = {
      columnDefs,
      rowData: rows,
      pagination: true,
      paginationPageSize: 50,
      paginationPageSizeSelector: [25, 50, 100, 200],
      domLayout: "autoHeight",
      defaultColDef: {
        resizable: true,
        sortable: true,
        filter: true,
        floatingFilter: true,
        minWidth: 90,
      },
      animateRows: true,
      overlayNoRowsTemplate:
        `<span style="padding:20px;color:#6B7280;">No BIN ranges match.</span>`,
    };

    if (window.agGrid && window.agGrid.createGrid) {
      this.gridApi = window.agGrid.createGrid(container, gridOptions);
    } else {
      console.error("BinRangeAgGridHook: agGrid not found on window");
    }

    this.handleEvent("update_bin_range_data", ({ rows: newRows }) => {
      if (this.gridApi) {
        this.gridApi.setGridOption("rowData", newRows || []);
      }
    });
  },

  updated() {
    if (this.gridApi && this.el.dataset.rows) {
      try {
        const rows = JSON.parse(this.el.dataset.rows);
        this.gridApi.setGridOption("rowData", rows);
      } catch (e) {
        console.warn("BinRangeAgGridHook updated(): failed to parse rows", e);
      }
    }
  },

  destroyed() {
    if (this.gridApi) {
      this.gridApi.destroy();
      this.gridApi = null;
    }
  },
};

export default BinRangeAgGridHook;
